import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import WaterChart from './components/WaterChart';
import FlowMeter from './components/FlowMeter';
import FlowChart from './components/FlowChart';
import DataCard, { type DataCardProps } from './components/DataCard';
import CardBox from '../../../CardBox';
import { Divider, Space, TimePicker } from 'antd';
import SwitchDate from './components/SwitchDate';
import { Button, DatePicker, message, SubContent } from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import titlePng from '@/assets/images/title_icon.png';
import { recent5Weeks, onChangePicker, disableFlowDate, TimePickerUtil, defaultNodeRangeValue } from './utils';
import { TimeActive, RangeValue, FlowIdContextProps, DeviceRankItem } from './type';
import {
  apiV2EnergyCompareGetMediumIndicatorLossDataLossDetailPost,
  apiV2PipelineLossProcessDataPost,
  apiV2PipelineLossRankingAnalysisPost,
  apiV2PipelineLossWaterBalancePost,
  apiV2WaterFlowConfigDetailPost,
  apiV2WaterFlowDataLeftPost,
  apiV2WaterFlowDataRightChartHourPost,
  apiV2WaterFlowDataRightStatisticPost,
  apiV2WaterMapDataWaterFlowMonitoringPost,
  V2PipelineLossProcessDataPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { ShareConfigDataWaterContext } from '../..';
import { DoubleRightOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
export const flowIdContext = createContext<FlowIdContextProps>({
  flowId: undefined,
  flowTitle: undefined,
  nodeId: undefined,
  nodeTitle: undefined,
  flowDate: dayjs().valueOf(),
  setFlowDate: () => {},
  setNodeId: () => {},
  setNodeTitle: () => {},
  setFlowTitle: () => {},
  setFlowId: () => {},
});
export interface TimeRange {
  monitorStartTime: string;
  monitorEndTime: string;
}
const RightBox: React.FC = () => {
  const { configDetail } = useContext(ShareConfigDataWaterContext);
  const { loadMap } = useContext(ShareDataWrapperContext);
  const isNoMapLayout = !loadMap;
  const RangePicker = DatePicker.RangePicker;
  const [activeKey, setActiveKey] = useState<TimeActive>(TimeActive.CUSTOM); // 选择的switch时间范围
  const [nodeRangeValue, setNodeRangeValue] = useState<RangeValue>([dayjs().startOf('month'), dayjs().endOf('day')]); // datepicker的时间范围
  const [nodeTitle, setNodeTitle] = useState<string | undefined>();
  const [nodeId, setNodeId] = useState<number | undefined>();
  const [flowId, setFlowId] = useState<number | undefined>();
  const [flowTitle, setFlowTitle] = useState<string | undefined>();
  const [pipeLossRankData, setPipeLossRankData] = useState<any[]>([]); //管损排名数据
  const [pipeLossChartData, setPipeLossChartData] = useState<V2PipelineLossProcessDataPostResponse>(); //管损图表数据
  const [flowDate, setFlowDate] = useState<number>();
  const [queryTimeRanges, setQueryTimeRanges] = useState<TimeRange[]>();
  const [flowRangeValue, setFlowRangeValue] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('day'),
  ]); // 添加长流水监测的时间范围状态
  const [flowDataList, setFlowDataList] = useState<any[]>([]); // 长流水监测详情列表
  const [selectedDevice, setSelectedDevice] = useState<DeviceRankItem>();
  const picker = useMemo(() => TimePickerUtil(activeKey), [activeKey]);

  useEffect(() => {
    // 获取水平衡分析数据
    apiV2PipelineLossWaterBalancePost({
      from: nodeRangeValue[0].valueOf(),
      to: nodeRangeValue[1].endOf('day').valueOf(),
    }).then(res => {
      const list = (res.list || []).map((item, index) => ({
        id: index + 1,
        name: item.name || '--',
        lossData: item.deviationAmount,
        lossPercentage: item.deviationRate,
        unitName: item.unitName,
      }));
      setPipeLossRankData(list);
      if (list.length > 0) {
        setNodeId(list[0].id);
        setNodeTitle(list[0].name);
      }
    });
  }, [nodeRangeValue]);

  //useEffect(() => defaultNodeRangeValue(activeKey, setNodeRangeValue), [activeKey]); //

  useEffect(() => {
    // 获取管损图表数据
    if (!nodeId) return;
    console.log('nodeId', nodeId);
    apiV2PipelineLossProcessDataPost({
      from: nodeRangeValue[0].valueOf(),
      to: nodeRangeValue[1].endOf('day').valueOf(),
      id: nodeId,
      type: activeKey,
    }).then(res => {
      setPipeLossChartData(res || []);
    });
  }, [nodeRangeValue, nodeId]);

  // 配置详情
  const { data: configData } = useRequest(() => apiV2WaterFlowConfigDetailPost({}), {
    onSuccess: res => {
      if (res.timePeriods) {
        setQueryTimeRanges(
          res.timePeriods.map(time => ({
            monitorStartTime: time.monitorStartTime!.split(':').slice(0, 2).join(':'),
            monitorEndTime: time.monitorEndTime!.split(':').slice(0, 2).join(':'),
          }))
        );
      }
    },
  });
  const { data: leftData } = useRequest(
    () =>
      apiV2WaterFlowDataLeftPost({
        startTime: flowRangeValue[0] ? flowRangeValue[0].startOf('day').valueOf() : undefined,
        endTime: flowRangeValue[1] ? flowRangeValue[1].endOf('day').valueOf() : undefined,
        timePeriods: queryTimeRanges,
      }),
    {
      refreshDeps: [flowRangeValue, queryTimeRanges],
      ready: !!queryTimeRanges,
      onSuccess: res => {
        const devices = res?.dataList || [];
        setSelectedDevice(devices.length > 0 ? { ...devices[0], rank: 1 } : undefined);
      },
    }
  );
  const deviceList = useMemo<DeviceRankItem[]>(() => {
    return (leftData?.dataList || []).map((device, index) => ({ ...device, rank: index + 1 }));
  }, [leftData?.dataList]);

  // 右侧统计数据请求
  const { data: rightStatisticData } = useRequest(
    () =>
      apiV2WaterFlowDataRightStatisticPost({
        startTime: flowRangeValue[0] ? flowRangeValue[0].startOf('day').valueOf() : undefined,
        endTime: flowRangeValue[1] ? flowRangeValue[1].endOf('day').valueOf() : undefined,
        timePeriods: queryTimeRanges,
        deviceId: selectedDevice?.deviceId,
      }),
    {
      refreshDeps: [flowRangeValue, queryTimeRanges, selectedDevice?.deviceId],
      ready: !!selectedDevice?.deviceId && !!queryTimeRanges,
    }
  );

  // 右侧图表数据请求 - 小时维度
  const { data: chartHourData } = useRequest(
    () =>
      apiV2WaterFlowDataRightChartHourPost({
        startTime: flowRangeValue[0] ? flowRangeValue[0].startOf('day').valueOf() : undefined,
        endTime: flowRangeValue[1] ? flowRangeValue[1].endOf('day').valueOf() : undefined,
        timePeriods: queryTimeRanges,
        deviceId: selectedDevice?.deviceId,
        resolution: 'H',
      }),
    {
      refreshDeps: [flowRangeValue, queryTimeRanges, selectedDevice?.deviceId],
      ready: !!selectedDevice?.deviceId && !!queryTimeRanges,
    }
  );

  return (
    <flowIdContext.Provider
      value={{
        flowId,
        setFlowId,
        flowTitle,
        setFlowTitle,
        nodeId,
        setNodeId,
        nodeTitle,
        setNodeTitle,
        flowDate,
        setFlowDate,
      }}
    >
      <CardBox
        title="用水分析"
        className={isNoMapLayout ? styles.card_content_no_map : ''}
        style={{
          height: isNoMapLayout ? 'auto' : undefined,
        }}
      >
        <div className={styles.titleRow} style={{ margin: '16px 0 12px' }}>
          <img src={titlePng} alt="" />
          <div>水平衡分析</div>
          <Space size={8} style={{ marginLeft: 'auto' }}>
            <RangePicker
              picker={picker}
              value={nodeRangeValue}
              getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
              disabledDate={current => current && current > dayjs().startOf('day')}
              onChange={dates => {
                if (!dates || !dates[0] || !dates[1]) return;
                onChangePicker(dates as RangeValue, activeKey, setNodeRangeValue);
              }}
              allowClear={false}
            />
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                window.open(
                  `/energyMedia/topology/analysis?nodeId=${nodeId}&from=${nodeRangeValue[0].valueOf()}&to=${nodeRangeValue[1]
                    .endOf('day')
                    .valueOf()}&activeKey=${activeKey}`,
                  '_blank'
                );
              }}
            >
              <DoubleRightOutlined className="translationStyles" />
            </Button>
          </Space>
        </div>
        <DataCard data={pipeLossRankData} />
        <div style={{ width: '100%', borderBottom: '1px dashed rgba(255, 255, 255, 0.2)', marginBottom: 12 }}></div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <div className={styles.titleRow}>
            <img src={titlePng} alt="" />
            <div>常用水监测</div>
          </div>
          <RangePicker
            value={flowRangeValue}
            getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
            disabledDate={current => disableFlowDate(current, flowRangeValue)}
            onChange={dates => {
              if (dates && dates[0] && dates[1]) {
                let start = dates[0];
                let end = dates[1];
                if (start.isSame(end, 'day')) {
                  start = start.startOf('day');
                  end = end.endOf('day');
                }
                const diffDays = end.diff(start, 'days');
                if (diffDays > 30) {
                  message.error('最多只能选择31天的时间范围');
                  return;
                }
                setFlowRangeValue([start, end]);
              } else if (dates) {
                setFlowRangeValue(dates as [Dayjs, Dayjs]);
              }
            }}
            allowClear={false}
          />
        </div>
        <FlowChart
          dateRange={flowRangeValue}
          selectedDevice={selectedDevice}
          deviceList={deviceList}
          onDeviceChange={setSelectedDevice}
          rightStatisticData={rightStatisticData}
          pageLabel={configData?.description}
          chartHourData={chartHourData}
        />
      </CardBox>
    </flowIdContext.Provider>
  );
};

export default RightBox;
